/**
 * Utility functions for device detection
 * Detects if the site is being accessed from a mobile device
 * (not just screen size, but actual device type)
 */

/**
 * Check if the device is a mobile device based on user agent
 * This detects actual mobile devices, not just screen size
 * Works even when mobile browser is in desktop mode
 * @returns {boolean} True if device is mobile/tablet
 */
export const isMobileDevice = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for mobile device patterns in user agent (most reliable)
  // This works even when browser is in desktop mode
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet|fennec|windows phone|windows mobile|kindle|silk|meego|bb10|playbook/i;
  
  // Check user agent first (primary detection method)
  if (mobileRegex.test(userAgent)) {
    return true;
  }
  
  // Additional checks for mobile devices even in desktop mode
  // Check platform - mobile devices often report mobile platforms
  const platform = navigator.platform?.toLowerCase() || '';
  if (/android|iphone|ipad|ipod|mobile|tablet/i.test(platform)) {
    return true;
  }
  
  // Check for touch support combined with screen characteristics
  // Most mobile devices have touch screens
  const hasTouchScreen = 'ontouchstart' in window || 
                         navigator.maxTouchPoints > 0 || 
                         navigator.msMaxTouchPoints > 0;
  
  // Check screen dimensions - mobile devices typically have smaller screens
  // Even in desktop mode, the physical screen size remains small
  const screenWidth = window.screen.width || window.innerWidth;
  const screenHeight = window.screen.height || window.innerHeight;
  const isSmallScreen = screenWidth <= 1024 || screenHeight <= 768;
  
  // Check device pixel ratio - mobile devices often have high DPI
  const hasHighDPI = window.devicePixelRatio && window.devicePixelRatio > 2;
  
  // If it has touch AND (small screen OR high DPI), likely mobile
  // But exclude known desktop OS patterns
  if (hasTouchScreen && (isSmallScreen || hasHighDPI)) {
    // Exclude desktop OS patterns
    const desktopOSPattern = /windows nt|macintosh|linux|ubuntu|fedora|debian/i;
    if (!desktopOSPattern.test(userAgent) && !desktopOSPattern.test(platform)) {
      return true;
    }
  }
  
  // Check for mobile-specific APIs or properties
  // Some mobile browsers expose these even in desktop mode
  if (navigator.standalone !== undefined || window.navigator.standalone !== undefined) {
    return true; // iOS standalone mode
  }
  
  return false;
};

/**
 * Check if device is a tablet specifically
 * @returns {boolean} True if device is a tablet
 */
export const isTabletDevice = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Tablets typically have "tablet" in user agent or are iPad
  const tabletRegex = /ipad|tablet|playbook|silk|android(?!.*mobile)/i;
  
  return tabletRegex.test(userAgent);
};

